'use client';

import { GoChevronDown } from '@react-icons/all-files/go/GoChevronDown';
import { AnimatePresence, motion } from 'framer-motion';
import { Link } from 'gatsby';
import { ChevronRight } from 'lucide-react';
import type { FC } from 'react';
import { useEffect, useRef, useState } from 'react';
import type { NavLink } from '../../../@types/app';
import { navLinks } from '../../../data/nav';
import useHandleClose from '../../../hooks/use-handle-close';
import { useHeaderMenuBackdrop } from '../header-menu-context';
import ProductDrilldown from '../navigation/product-drilldown';

type BurgerMenuProps = {
  onRequestQuote: () => void;
};

type MobilePanelView = 'main' | 'products';

const BurgerMenu: FC<BurgerMenuProps> = ({ onRequestQuote }) => {
  const [open, setOpen] = useState(false);
  useHeaderMenuBackdrop(open);
  const [panelView, setPanelView] = useState<MobilePanelView>('main');
  const ref = useRef<HTMLDivElement>(null);

  const topBarVariants = {
    open: { rotate: 45, translateY: 7 },
    closed: { rotate: 0, translateY: 0 },
  };

  const middleBarVariants = {
    open: { opacity: 0 },
    closed: { opacity: 1 },
  };

  const bottomBarVariants = {
    open: { rotate: -45, translateY: -7 },
    closed: { rotate: 0, translateY: 0 },
  };

  useHandleClose(() => {
    setOpen(false);
  }, ref);

  useEffect(() => {
    if (!open) {
      setPanelView('main');
    }
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  const closeMenu = () => {
    setOpen(false);
    setPanelView('main');
  };

  const mainLinks: NavLink[] = [
    { href: '/', name: 'HOME' },
    ...navLinks,
  ];

  return (
    <div className="site-header__burger lg:hidden" ref={ref}>
      <button
        aria-expanded={open}
        aria-label={open ? 'Close menu' : 'Open menu'}
        className="relative z-20 h-4 w-6 cursor-pointer"
        onClick={() => {
          setOpen(!open);
        }}
        type="button"
      >
        <motion.div
          animate={open ? 'open' : 'closed'}
          className="absolute top-0 h-px w-full bg-action-btn"
          variants={topBarVariants}
        />
        <motion.div
          animate={open ? 'open' : 'closed'}
          className="absolute top-1/2 h-[1.5px] w-full -translate-y-1/2 bg-action-btn"
          variants={middleBarVariants}
        />
        <motion.div
          animate={open ? 'open' : 'closed'}
          className="absolute bottom-0 h-px w-full bg-action-btn"
          variants={bottomBarVariants}
        />
      </button>

      <AnimatePresence>
        {open ? (
          <motion.div
            animate={{ opacity: 1 }}
            className="site-header__mobile-panel site-header__mobile-panel--fullscreen fixed inset-x-0 bottom-0 z-40 flex flex-col bg-white"
            exit={{ opacity: 0 }}
            initial={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
          >
            <div className="product-drilldown__panel-body">
              <AnimatePresence mode="wait">
                {panelView === 'main' ? (
                  <motion.div
                    animate={{ opacity: 1, x: 0 }}
                    className="site-header__mobile-main"
                    exit={{ opacity: 0, x: -24 }}
                    initial={{ opacity: 0, x: -24 }}
                    key="main"
                    transition={{ duration: 0.2 }}
                  >
                    <ul className="site-header__mobile-list">
                      <li className="w-full">
                        <Link
                          className="site-header__mobile-link"
                          onClick={closeMenu}
                          to="/"
                        >
                          HOME
                        </Link>
                      </li>

                      <li className="w-full">
                        <button
                          className="site-header__mobile-link site-header__mobile-link--drill w-full text-left"
                          onClick={() => {
                            setPanelView('products');
                          }}
                          type="button"
                        >
                          <span>PRODUCTS</span>
                          <ChevronRight aria-hidden size={18} strokeWidth={2} />
                        </button>
                      </li>

                      {mainLinks.slice(1).map(({ href, name, submenu }) => {
                        if (submenu) {
                          return (
                            <li className="w-full" key={name}>
                              <DropdownMenu
                                name={name}
                                onClick={closeMenu}
                                submenu={submenu}
                              />
                            </li>
                          );
                        }

                        return (
                          <li className="w-full" key={name}>
                            <Link
                              className="site-header__mobile-link"
                              onClick={closeMenu}
                              to={href}
                            >
                              {name}
                            </Link>
                          </li>
                        );
                      })}

                      <li className="w-full pt-2">
                        <button
                          className="site-header__cta site-header__cta--mobile w-full"
                          onClick={() => {
                            closeMenu();
                            onRequestQuote();
                          }}
                          type="button"
                        >
                          Request Quote
                        </button>
                      </li>
                    </ul>
                  </motion.div>
                ) : (
                  <motion.div
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 24 }}
                    initial={{ opacity: 0, x: 24 }}
                    key="products"
                    transition={{ duration: 0.2 }}
                  >
                    <ProductDrilldown
                      onBackToMain={() => {
                        setPanelView('main');
                      }}
                      onClose={closeMenu}
                    />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
};

export default BurgerMenu;

const DropdownMenu: FC<NavLink & { onClick: () => void }> = ({
  name,
  submenu,
  onClick,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLButtonElement>(null);

  useHandleClose(() => {
    setMenuOpen(false);
  }, menuRef);

  return (
    <>
      <button
        className="site-header__mobile-link w-full text-left"
        onClick={() => {
          setMenuOpen(!menuOpen);
        }}
        ref={menuRef}
        type="button"
      >
        <span>{name}</span>
        <GoChevronDown
          className={`transform text-md transition-transform ${menuOpen ? 'rotate-180' : ''}`}
        />
      </button>
      <AnimatePresence mode="wait">
        {submenu && menuOpen ? (
          <motion.div
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            initial={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="site-header__mobile-sublist">
              {submenu.map(({ href, name: linkName }) => (
                <Link
                  className="site-header__mobile-sublink"
                  key={linkName}
                  onClick={() => {
                    onClick();
                  }}
                  to={href}
                >
                  {linkName}
                </Link>
              ))}
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </>
  );
};
